
import {
  defineComponent,
  computed,
  PropType,
  ref,
  watch,
} from 'vue';
import Sidebar from 'primevue/sidebar';
import Dropdown from 'primevue/dropdown';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { useI18n } from 'vue-i18n';
import type { SpmFieldConfig, SpmActiveSort } from './types';
import type { SpmActiveFilter } from './filters';
import { SpmFilterInput, SpmSortInput } from './filters/components';

export default defineComponent({
  name: 'SpmTableApiControlsPanel',

  components: {
    Sidebar,
    Dropdown,
    SpmButton,
    SpmFilterInput,
    SpmSortInput,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },

    sortableFields: {
      type: Array as PropType<SpmFieldConfig[]>,
      default: () => [],
    },

    filterableFields: {
      type: Array as PropType<SpmFieldConfig[]>,
      default: () => [],
    },

    // Current active filters from parent
    activeFilters: {
      type: Object as PropType<Map<string, SpmActiveFilter>>,
      default: () => new Map(),
    },

    // Current active sort from parent
    activeSort: {
      type: Object as PropType<SpmActiveSort | null>,
      default: null,
    },

    // Désactive la section "Tri" du panel quand le back tourne en mode
    // pagination optimisée (ORDER BY retiré). Les filtres restent dispos
    // car ils continuent d'être appliqués par le back. Cf. /stats/widget-data
    // avec includeCount: false ou auto-bascule au-delà de 50000 lignes.
    sortingDisabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:visible', 'apply', 'reset'],

  setup(props, { emit }) {
    interface FilterDropdownOption {
      name: string;
      label: string;
      displayLabel: string;
      kind: 'local' | 'relation-base' | 'relation-sub';
      sourceField?: string;
    }

    interface FilterDropdownGroup {
      kind: 'local-group' | 'relation-group';
      label: string;
      items: FilterDropdownOption[];
    }

    const { t } = useI18n();

    // Two-way binding for visible prop
    const internalVisible = computed({
      get: () => props.visible,
      set: (value: boolean) => emit('update:visible', value),
    });

    // Local copy of filters for editing (before apply)
    const localFilters = ref<Map<string, SpmActiveFilter>>(new Map());

    // Local copy of sort for editing (before apply)
    const localSort = ref<SpmActiveSort | null>(null);

    // Track which filter fields are currently shown (added by user)
    const shownFilterFields = ref<Set<string>>(new Set());

    // Selected filter to add (for dropdown)
    const selectedFilterToAdd = ref<string | null>(null);

    // Computed: fields that are currently shown as filters
    const activeFilterFields = computed(() => props.filterableFields.filter(
      (field) => shownFilterFields.value.has(field.name),
    ));

    const toLabel = (value: string): string => value
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());

    const normalizeRelationSubLabel = (
      rawLabel: string,
      sourceLabel: string,
      optionName: string,
    ): string => {
      const sourcePrefix = `${sourceLabel} - `;
      if (rawLabel.startsWith(sourcePrefix)) {
        return rawLabel.slice(sourcePrefix.length).trim();
      }
      if (rawLabel.includes(' - ')) {
        return rawLabel.split(' - ').slice(1).join(' - ').trim();
      }
      if (optionName.includes('__rel__')) {
        return toLabel(optionName.split('__rel__')[1]);
      }
      return rawLabel;
    };

    const availableFiltersGrouped = computed<FilterDropdownGroup[]>(() => {
      const availableFields = props.filterableFields
        .filter((field) => !shownFilterFields.value.has(field.name));

      const relationEntries = availableFields
        .filter((field) => !!field.relationFilterConfig);
      const relationBySource = relationEntries.reduce(
        (accumulator: Record<string, SpmFieldConfig[]>, field) => {
          const sourceField = field.relationFilterConfig?.sourceField;
          if (!sourceField) return accumulator;
          if (!accumulator[sourceField]) {
            accumulator[sourceField] = [];
          }
          accumulator[sourceField].push(field);
          return accumulator;
        },
        {},
      );

      const relationSourceFields = new Set(Object.keys(relationBySource));
      const localOptions: FilterDropdownOption[] = [];
      const relationGroups: FilterDropdownGroup[] = [];

      availableFields.forEach((field) => {
        const fieldLabel = field.label || field.name;
        if (field.relationFilterConfig) return;
        if (relationSourceFields.has(field.name)) return;

        localOptions.push({
          name: field.name,
          label: fieldLabel,
          displayLabel: fieldLabel,
          kind: 'local',
        });
      });

      Object.keys(relationBySource)
        .sort((sourceA, sourceB) => {
          const labelA = props.filterableFields.find((field) => field.name === sourceA)?.label || sourceA;
          const labelB = props.filterableFields.find((field) => field.name === sourceB)?.label || sourceB;
          return labelA.localeCompare(labelB);
        })
        .forEach((sourceField) => {
          const sourceConfig = props.filterableFields.find((field) => field.name === sourceField);
          const sourceLabel = sourceConfig?.label || sourceField;
          const groupItems: FilterDropdownOption[] = [];

          if (sourceConfig && !shownFilterFields.value.has(sourceConfig.name)) {
            groupItems.push({
              name: sourceConfig.name,
              label: sourceLabel,
              displayLabel: sourceLabel,
              kind: 'relation-base',
              sourceField,
            });
          }

          relationBySource[sourceField]
            .sort((fieldA, fieldB) => (fieldA.label || fieldA.name).localeCompare(fieldB.label || fieldB.name))
            .forEach((field) => {
              const rawLabel = field.label || field.name;
              groupItems.push({
                name: field.name,
                label: rawLabel,
                displayLabel: normalizeRelationSubLabel(rawLabel, sourceLabel, field.name),
                kind: 'relation-sub',
                sourceField,
              });
            });

          if (groupItems.length > 0) {
            relationGroups.push({
              kind: 'relation-group',
              label: sourceLabel,
              items: groupItems,
            });
          }
        });

      localOptions.sort((optionA, optionB) => optionA.label.localeCompare(optionB.label));

      const groups: FilterDropdownGroup[] = [];
      if (localOptions.length > 0) {
        groups.push({
          kind: 'local-group',
          label: '',
          items: localOptions,
        });
      }
      groups.push(...relationGroups);
      return groups;
    });

    const availableFiltersCount = computed(() => availableFiltersGrouped.value
      .reduce((total, group) => total + group.items.length, 0));

    // Sync local state when panel opens
    watch(() => props.visible, (isVisible) => {
      if (isVisible) {
        // Copy current filters to local state
        localFilters.value = new Map(props.activeFilters);
        localSort.value = props.activeSort ? { ...props.activeSort } : null;
        // Show fields that have active filters
        shownFilterFields.value = new Set(props.activeFilters.keys());
      }
    });

    const closePanel = () => {
      emit('update:visible', false);
    };

    /**
     * Add a filter field to the shown list
     */
    const onAddFilter = () => {
      if (selectedFilterToAdd.value) {
        shownFilterFields.value.add(selectedFilterToAdd.value);
        selectedFilterToAdd.value = null;
      }
    };

    /**
     * Update a filter in local state
     */
    const updateFilter = (fieldName: string, filter: SpmActiveFilter | null) => {
      if (filter) {
        localFilters.value.set(fieldName, filter);
      } else {
        localFilters.value.delete(fieldName);
      }
    };

    /**
     * Remove a filter from local state and hide it
     */
    const removeFilter = (fieldName: string) => {
      localFilters.value.delete(fieldName);
      shownFilterFields.value.delete(fieldName);
    };

    const applyChanges = () => {
      // En mode pagination optimisée, le back ignore l'ORDER BY ; on ne
      // remonte donc pas le `localSort` pour éviter un état fantôme
      // (chip de tri visible mais sans effet).
      const sortToEmit = props.sortingDisabled ? null : localSort.value;
      emit('apply', localFilters.value, sortToEmit);
      closePanel();
    };

    const resetChanges = () => {
      // Clear local state
      localFilters.value.clear();
      localSort.value = null;
      emit('reset');
      closePanel();
    };

    // Check if there are any changes to apply (deep comparison)
    const hasChanges = computed(() => {
      // Convert Maps to arrays for JSON comparison
      const localFiltersArray = Array.from(localFilters.value.entries());
      const propsFiltersArray = Array.from(props.activeFilters.entries());

      const filtersChanged = JSON.stringify(localFiltersArray) !== JSON.stringify(propsFiltersArray);
      const sortChanged = JSON.stringify(localSort.value) !== JSON.stringify(props.activeSort);

      return filtersChanged || sortChanged;
    });

    return {
      t,
      internalVisible,
      localFilters,
      localSort,
      activeFilterFields,
      availableFiltersGrouped,
      availableFiltersCount,
      selectedFilterToAdd,
      onAddFilter,
      updateFilter,
      removeFilter,
      applyChanges,
      resetChanges,
      hasChanges,
    };
  },
});
